import { Radio, RadioChangeEvent, RadioProps } from 'antd';
import clx from 'classnames';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useController } from 'react-hook-form';
import styled from 'styled-components';
import { useField } from '../field/useField';
import { useOnFieldDisabled } from '../hooks/use-on-field-disabled/useOnFieldDisabled';
import { useFormItemEnabled } from '../item/useFormItemEnabled';

const StyledRadio = styled(Radio)`
  &.read-only {
    pointer-events: none;
  }
`;

export interface IRadioOptionInputProps extends Omit<RadioProps, 'name'> {
  name: string;
  optionValue: string | number;
  /**
   * Permette di deselezionare l'opzione premendo sull'opzione stessa.
   */
  allowUnselect?: boolean;
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
  /**
   * in caso che dobbiamo trasformare il valore per l'input
   */
  transformValue?: (value: any) => any;
}

export function RadioOptionInput(props: IRadioOptionInputProps) {
  const {
    name,
    optionValue,
    allowUnselect,
    readOnly,
    className,
    transformValue,
    ...inputProps
  } = props;

  const { enabled: parentEnabled, layoutEnabled: parentLayoutEnabled } =
    useFormItemEnabled();

  const {
    enabled: groupEnabled,
    layoutEnabled: groupLayoutEnabled,
    visible,
    schema,
    unregister,
  } = useField(name);

  // Flag di abilitazione del campo
  const enabled = useMemo(
    () => parentEnabled !== false && groupEnabled !== false,
    [groupEnabled, parentEnabled],
  );

  // Flag di abilitazione UI/UX del campo
  // A diferrenza di enabled, blocca l'edit solo a livello di layout
  // ma non modifica le logiche di reset/validazione.
  // Utilizzato, ad esempio, per disabilitare un campo dopo la conferma di un ConfirmWidget.
  const layoutEnabled = useMemo(
    () => parentLayoutEnabled !== false && groupLayoutEnabled !== false,
    [groupLayoutEnabled, parentLayoutEnabled],
  );

  const isDisabled = useMemo(() => {
    // È disabilitato se almeno uno dei flag è false
    // o se è indicato dalle inputProps
    return enabled === false || layoutEnabled === false || inputProps.disabled;
  }, [enabled, inputProps.disabled, layoutEnabled]);

  const {
    field: { value: currentValue, onChange, onBlur, ref, ...others },
  } = useController({ name });
  const transformedValue = transformValue?.(currentValue) ?? currentValue;

  useEffect(() => {
    if (visible) return;
    unregister();
  }, [unregister, visible]);

  // Gestisco la disabilitazione del campo
  useOnFieldDisabled(
    { value: transformedValue, onChange, onBlur, ref, ...others },
    !isDisabled,
    (methods, name) => {
      // Se disattivo la singola opzione e non è selezionata, non faccio nulla
      if (inputProps.disabled && transformedValue !== optionValue) {
        return;
      }

      // Se è disabilitato a livello di UX/UI, non faccio nulla
      if (enabled && !layoutEnabled) {
        return;
      }

      // Se è stata disabilitata l'intera select o l'opzione disabilitata è anche quella selezionata
      // reimposto a default.
      let resetValue: string | null | undefined = schema?.[0]?.getDefault();

      // Se disattivo la singola opzione ed è quella selezionata la imposto a null, non faccio nulla
      if (inputProps.disabled && transformedValue === optionValue) {
        resetValue = null;
      }

      methods.setValue(name, resetValue, {
        shouldDirty: false,
        shouldValidate: true,
        shouldTouch: false,
      });
    },
  );

  const handleChange = useCallback(
    (e: RadioChangeEvent) => {
      onChange(e.target.value);
      onBlur();
    },
    [onChange, onBlur],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      // Permette al campo di essere deselezionato premendo sulla stessa opzione.
      if (allowUnselect && transformedValue === optionValue) {
        onChange(null);
        onBlur();
        // Disabilitiamo l'onChange che "riattiverebbe" il campo
        e.preventDefault();
      }
    },
    [allowUnselect, optionValue, transformedValue, onChange, onBlur],
  );

  const checked = useMemo(() => {
    return optionValue === transformedValue;
  }, [optionValue, transformedValue]);

  if (!visible) return null;

  return (
    <StyledRadio
      {...inputProps}
      disabled={isDisabled ?? inputProps.disabled}
      ref={ref}
      onChange={readOnly ? undefined : handleChange}
      onClick={readOnly ? undefined : handleClick}
      value={optionValue}
      checked={checked}
      className={clx(className, { 'read-only': readOnly })}
    />
  );
}
